import { useEntity } from '@backstage/plugin-catalog-react';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import TrendingFlatIcon from '@material-ui/icons/TrendingFlat';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import Alert from '@material-ui/lab/Alert';
import React from 'react';
import useAsync from 'react-use/esm/useAsync';
import { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Line } from 'recharts';
import { codeCoverageApiRef } from '../../api.esm.js';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { DateTime } from 'luxon';

const useStyles = makeStyles((theme) => ({
  trendDown: {
    color: theme.palette.status.warning
  },
  trendUp: {
    color: theme.palette.status.ok
  }
}));
const getTrendIcon = (trend, classes) => {
  switch (true) {
    case trend > 0:
      return /* @__PURE__ */ React.createElement(TrendingUpIcon, { className: classes.trendUp });
    case trend < 0:
      return /* @__PURE__ */ React.createElement(TrendingDownIcon, { className: classes.trendDown });
    case trend === 0:
    default:
      return /* @__PURE__ */ React.createElement(TrendingFlatIcon, null);
  }
};
function formatDateToHuman(timeStamp) {
  return DateTime.fromMillis(Number(timeStamp)).toLocaleString(
    DateTime.DATETIME_MED
  );
}
const CoverageHistoryChart = () => {
  const { entity } = useEntity();
  const codeCoverageApi = useApi(codeCoverageApiRef);
  const {
    loading: loadingHistory,
    error: errorHistory,
    value: valueHistory
  } = useAsync(
    async () => await codeCoverageApi.getCoverageHistoryForEntity({
      kind: entity.kind,
      namespace: entity.metadata.namespace || "default",
      name: entity.metadata.name
    })
  );
  const classes = useStyles();
  if (loadingHistory) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  }
  if (errorHistory) {
    return /* @__PURE__ */ React.createElement(ResponseErrorPanel, { error: errorHistory });
  } else if (!valueHistory) {
    return /* @__PURE__ */ React.createElement(Alert, { severity: "warning" }, "No history found.");
  }
  if (!valueHistory.history.length) {
    return /* @__PURE__ */ React.createElement(Card, null, /* @__PURE__ */ React.createElement(CardHeader, { title: "History" }), /* @__PURE__ */ React.createElement(CardContent, null, "No coverage history found"));
  }
  const [oldestCoverage] = valueHistory.history.slice(-1);
  const latestCoverage = valueHistory.history[0];
  const getTrendForCoverage = (type) => {
    if (!oldestCoverage[type].percentage) {
      return 0;
    }
    return (latestCoverage[type].percentage - oldestCoverage[type].percentage) / oldestCoverage[type].percentage * 100;
  };
  const lineTrend = getTrendForCoverage("line");
  const branchTrend = getTrendForCoverage("branch");
  return /* @__PURE__ */ React.createElement(Card, null, /* @__PURE__ */ React.createElement(CardHeader, { title: "History" }), /* @__PURE__ */ React.createElement(CardContent, null, /* @__PURE__ */ React.createElement(Box, { px: 6, display: "flex" }, /* @__PURE__ */ React.createElement(Box, { display: "flex", mr: 4 }, getTrendIcon(lineTrend, classes), /* @__PURE__ */ React.createElement(Typography, null, "Current line: ", latestCoverage.line.percentage, "%", /* @__PURE__ */ React.createElement("br", null), "(", Math.floor(lineTrend), "% change over ", valueHistory.history.length, " ", "builds)")), /* @__PURE__ */ React.createElement(Box, { display: "flex" }, getTrendIcon(branchTrend, classes), /* @__PURE__ */ React.createElement(Typography, null, "Current branch: ", latestCoverage.branch.percentage, "%", /* @__PURE__ */ React.createElement("br", null), "(", Math.floor(branchTrend), "% change over", " ", valueHistory.history.length, " builds)"))), /* @__PURE__ */ React.createElement(ResponsiveContainer, { width: "100%", height: 300 }, /* @__PURE__ */ React.createElement(
    LineChart,
    {
      data: valueHistory.history,
      margin: { right: 48, top: 32 }
    },
    /* @__PURE__ */ React.createElement(CartesianGrid, { strokeDasharray: "3 3" }),
    /* @__PURE__ */ React.createElement(
      XAxis,
      {
        dataKey: "timestamp",
        tickFormatter: formatDateToHuman,
        reversed: true
      }
    ),
    /* @__PURE__ */ React.createElement(YAxis, { dataKey: "line.percentage" }),
    /* @__PURE__ */ React.createElement(YAxis, { dataKey: "branch.percentage" }),
    /* @__PURE__ */ React.createElement(Tooltip, { labelFormatter: formatDateToHuman }),
    /* @__PURE__ */ React.createElement(Legend, null),
    /* @__PURE__ */ React.createElement(
      Line,
      {
        type: "monotone",
        dataKey: "branch.percentage",
        stroke: "#8884d8"
      }
    ),
    /* @__PURE__ */ React.createElement(Line, { type: "monotone", dataKey: "line.percentage", stroke: "#82ca9d" })
  ))));
};

export { CoverageHistoryChart };
//# sourceMappingURL=CoverageHistoryChart.esm.js.map
